import React, { FC, useEffect, useState } from 'react';
import { CloneButton, EditButton, useDataProvider, useNotify, usePermissions } from 'react-admin';
import { useDispatch } from "react-redux";
import { Card, TableContainer, Table, TableHead, TableRow, TableCell, Box, TableBody, Dialog, DialogContent } from '@material-ui/core';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import DoneOutlineIcon from '@material-ui/icons/DoneOutline';

import { Prize, PrizeCategory } from '../../types/prize';
import FilterComponent from '../common/tablesFilter/FilterList';
import { removeMark, setMark } from '../../redux/actions/propertyActions';
import HeadTable from '../common/sortTable/sortTable';
import { searchSelect, selectedAfterSearch, selectedFilterItems, selectSelectedFilter } from '../../helpers/filter';
import PrizeListTitle from '../common/listTitles/PrizeListTitle';
import { getFieldByTitle } from '../../helpers/sortByTitle';
import PaginationTable from '../common/paginationTable';
import { checkPermission } from '../../helpers/checkPermissions';

import "./index.css"
import RemoveItem from '../common/Remove/RemoveItem';

const selectDropdown = (title: string) => {
    switch (title) {
        case 'Sub-Category':
            return 'subCategoryFilter';
        case 'Category':
            return 'categoryFilter';
        case 'Title':
            return 'titleFilter';
        default:
            return ''
    }
}

const getSelectedFilters = (prizes: Array<Prize>) => {

    const categories = Array<PrizeCategory>();
    const subCategories = Array<PrizeCategory>();
    prizes.map((item) => {
        let categoryExcist = false
        let subCategoryExcist = false
        const category: PrizeCategory = {
            id: item.prizeCategory._id || '',
            title: item.prizeCategory.title || '',
            isActive: item.active
        }
        categories.map((cat) => {
            if (cat.id === category.id) {
                categoryExcist = true
            }
        })
        if (item.subCategory) {
            const subCategory: PrizeCategory = {
                id: item.subCategory._id,
                title: item.subCategory.title || '',
                isActive: item.active
            }
            subCategories.map((sub) => {
                if (sub.id === subCategory.id) {
                    subCategoryExcist = true
                }
            })
            if ((!subCategoryExcist && subCategory.title)) {
                subCategories.push(subCategory)
            }
        }
        if (!categoryExcist) {
            categories.push(category)
        }
    })
    return { categories, subCategories }
}

const PrizesList: FC = () => {
    let { permissions } = usePermissions()
    const dataProvider = useDataProvider()
    const dispatch = useDispatch();
    const notify = useNotify()

    const [state, setState] = useState({
        prizes: Array<Prize>(),
        total: 0,
        page: 1,
        perPage: 10,
        status: "All",
        field: '_id',
        order: 'asc',
    })

    const [filter, setFilter] = useState({
        categories: Array<PrizeCategory>(),
        subCategories: Array<PrizeCategory>(),
        prizes: Array<PrizeCategory>(),
        selectedPrizes: Array<string>(),
        selectedCategories: Array<string>(),
        selectedSubCategories: Array<string>(),
        categoriesForSearch: Array<PrizeCategory>(),
        subCategoriesForSearch: Array<PrizeCategory>(),
        prizesForSearch: Array<PrizeCategory>(),
    })

    const [dropdown, setDropdown] = useState({
        categoryFilter: false,
        subCategoryFilter: false,
        titleFilter: false
    })

    const [searchState, setSearchState] = useState({
        selectedPrizes: '',
        selectedCategories: '',
        selectedSubCategories: '',
    })

    const [modal, setModal] = useState({
        showModal: false,
        id: ''
    })

    useEffect(() => {
        dispatch(removeMark())
        getPrizes(1, 10, "All", '_id', 'asc', Array<string>(), Array<string>(), Array<string>())
        getAllCategories()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const markClone = () => {
        dispatch(setMark())
    }

    const getAllCategories = async () => {
        const categories = await getCategories('')
        const subCategories = await getSubCategories('', Array<string>())
        const prizes = await getPrizesForFilter('', Array<string>(), Array<string>())
        setFilter({
            ...filter,
            categories: categories.data,
            subCategories: subCategories.data,
            prizes: prizes.data,
            categoriesForSearch: categories.data,
            subCategoriesForSearch: subCategories.data,
            prizesForSearch: prizes.data
        })
    }

    const getCategories = async (searchString: string) => {
        const categories = await dataProvider.getList('prizeCategories', {
            filter: { email: searchString, isActive: 'all' },
            pagination: { page: 0, perPage: 0 },
            sort: { field: "id", order: "asc" },
        }) as any;
        return categories
    }

    const getSubCategories = async (searchString: string, categoriesIds: Array<string>) => {
        const subCategories = await dataProvider.getList(`subcategories`, {
            filter: { email: searchString, categoriesIds: categoriesIds },
            pagination: { page: 0, perPage: 0 },
            sort: { field: "id", order: "asc" },
        }) as any;
        return subCategories
    }

    const getPrizesForFilter = async (searchString: string, categoriesIds: Array<string>, subCategoriesIds: Array<string>) => {
        const prizes = await dataProvider.getPrizesForFilter({
            filter: { email: searchString, categoriesIds: categoriesIds, subCategoriesIds: subCategoriesIds, prizesIds: filter.selectedPrizes },
            pagination: { page: 0, perPage: 0 },
            sort: { field: '', order: '' },
        }) as any
        return prizes
    }

    const searchCategory = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchState({ ...searchState, selectedCategories: event.currentTarget.value })
        const categories = searchSelect(event.currentTarget.value, filter.categoriesForSearch)
        const selectedCategories = selectedAfterSearch(categories, filter.selectedCategories)
        setFilter({ ...filter, categories: categories, selectedCategories: selectedCategories })
    }

    const searchSubCategory = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchState({ ...searchState, selectedSubCategories: event.currentTarget.value })
        const subCategories = searchSelect(event.currentTarget.value, filter.subCategoriesForSearch)
        const selectedSubCategories = selectedAfterSearch(subCategories, filter.selectedSubCategories)
        setFilter({ ...filter, subCategories: subCategories, selectedSubCategories: selectedSubCategories })
    }

    const searchPrize = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchState({ ...searchState, selectedPrizes: event.currentTarget.value })
        const prizes = searchSelect(event.currentTarget.value, filter.prizesForSearch)
        const selectedPrizes = selectedAfterSearch(prizes, filter.selectedPrizes)
        setFilter({ ...filter, prizes: prizes, selectedPrizes: selectedPrizes })
    }

    const selectedCategories = (id: string, checked: boolean) => {
        const categories = selectedFilterItems(id, checked, filter.selectedCategories)
        setFilter({ ...filter, selectedCategories: categories })
    }

    const selectedSubCategories = (id: string, checked: boolean) => {
        const subCategories = selectedFilterItems(id, checked, filter.selectedSubCategories)
        setFilter({ ...filter, selectedSubCategories: subCategories })
    }

    const selectedPrizes = (id: string, checked: boolean) => {
        const prizes = selectedFilterItems(id, checked, filter.selectedPrizes)
        setFilter({ ...filter, selectedPrizes: prizes })
    }

    const applyCategoryFilter = async () => {
        await getPrizes(1, 10, state.status, state.field, state.order, filter.selectedCategories, filter.selectedPrizes, filter.selectedSubCategories)
        const prizes = await getPrizesForFilter('', filter.selectedCategories, filter.selectedSubCategories)
        const { subCategories, categories } = getSelectedFilters(prizes.data)
        const selectedSubCategories = selectSelectedFilter(filter.selectedSubCategories, subCategories)
        setFilter({
            ...filter,
            subCategories: subCategories,
            prizes: prizes.data,
            selectedSubCategories: selectedSubCategories,
            subCategoriesForSearch: subCategories,
            prizesForSearch: prizes.data,
            categories: filter.selectedCategories.length === 0 ? categories : filter.categories
        })
        setSearchState({ selectedCategories: '', selectedPrizes: '', selectedSubCategories: '' })
    }

    const applyTitleFilter = async () => {
        await getPrizes(1, 10, state.status, state.field, state.order, filter.selectedCategories, filter.selectedPrizes, filter.selectedSubCategories)
        const prizes = await getPrizesForFilter('', filter.selectedCategories, filter.selectedSubCategories)
        const { categories, subCategories } = getSelectedFilters(prizes.data)
        const selectedCategories = selectSelectedFilter(filter.selectedCategories, categories)
        const selectedSubCategories = selectSelectedFilter(filter.selectedSubCategories, subCategories)

        setFilter({
            ...filter,
            categories: categories,
            subCategories: subCategories,
            selectedCategories: selectedCategories,
            selectedSubCategories: selectedSubCategories,
            categoriesForSearch: categories,
            subCategoriesForSearch: subCategories,
            prizes: filter.selectedPrizes.length === 0 ? prizes.data : filter.prizes
        })
        setSearchState({ selectedCategories: '', selectedPrizes: '', selectedSubCategories: '' })
    }

    const applySubCategoriesFilter = async () => {
        await getPrizes(
            1,
            10,
            state.status,
            state.field,
            state.order,
            filter.selectedCategories,
            filter.selectedPrizes,
            filter.selectedSubCategories
        )
        const filterPrizes = await getPrizesForFilter('', filter.selectedCategories, filter.selectedSubCategories)
        const { categories, subCategories } = getSelectedFilters(filterPrizes.data)
        const selectedCategories = selectSelectedFilter(filter.selectedCategories, categories)
        setFilter({
            ...filter,
            prizes: filterPrizes.data,
            categories: categories,
            selectedCategories: selectedCategories,
            prizesForSearch: filterPrizes.data,
            categoriesForSearch: categories,
            subCategories: filter.selectedSubCategories.length === 0 ? subCategories : filter.subCategories
        })
        setSearchState({ selectedCategories: '', selectedPrizes: '', selectedSubCategories: '' })
    }

    const clearSelectedFilter = (clear: string) => {
        setFilter({ ...filter, [clear]: Array<string>() })
    }

    const selectAllCategories = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked
        let categories = filter.categories
        if (checked) {
            const selectCategories = categories.reduce((acc, item) => {
                acc.push(item.id)
                return acc
            }, Array<string>())
            setFilter({ ...filter, selectedCategories: selectCategories })
        } else {
            setFilter({ ...filter, selectedCategories: Array<string>() })
        }
    }

    const selectedAllPrizes = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked
        let prizes = filter.prizes
        if (checked) {
            const selectedPrizes = prizes.reduce((acc, item) => {
                acc.push(item.id)
                return acc
            }, Array<string>())
            setFilter({ ...filter, selectedPrizes: selectedPrizes })
        } else {
            setFilter({ ...filter, selectedPrizes: Array<string>() })
        }
    }

    const selectAllSubCategories = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked
        let subCategories = filter.subCategories
        if (checked) {
            const selectSubCategories = subCategories.reduce((acc, item) => {
                acc.push(item.id)
                return acc
            }, Array<string>())
            setFilter({ ...filter, selectedSubCategories: selectSubCategories })
        } else {
            setFilter({ ...filter, selectedSubCategories: Array<string>() })
        }
    }

    const getPrizes = async (
        page: number,
        perPage: number,
        status: string,
        field: string,
        order: string,
        categoriesIds: Array<string>,
        prizesIds: Array<string>,
        subCategoriesIds: Array<string>
    ) => {
        const prizes = await dataProvider.getList('prizes', {
            filter: { status: status, categoriesIds: categoriesIds, subCategoriesIds: subCategoriesIds, prizesIds: prizesIds },
            pagination: { page: page, perPage: perPage },
            sort: { field: field, order: order },
        }) as any
        setState({
            ...state,
            prizes: prizes.data,
            total: prizes.total,
            page: page,
            perPage: perPage,
            status: status,
            field: field,
            order: order.toLowerCase()
        })
        return prizes.data
    }

    const statusFilter = (status: string) => {
        getPrizes(1, state.perPage, status, state.field, state.order, filter.selectedCategories, filter.selectedPrizes, filter.selectedSubCategories)
    }

    const handleChangePage = async (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        getPrizes(newPage + 1, state.perPage, state.status, state.field, state.order, filter.selectedCategories, filter.selectedPrizes, filter.selectedSubCategories)
    };

    const handleLastPage = () => {
        const page = Math.ceil(state.total / state.perPage)
        getPrizes(page, state.perPage, state.status, state.field, state.order, filter.selectedCategories, filter.selectedPrizes, filter.selectedSubCategories)
    }

    const handleFirstPage = () => {
        getPrizes(1, state.perPage, state.status, state.field, state.order, filter.selectedCategories, filter.selectedPrizes, filter.selectedSubCategories)
    }

    const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const perPage = parseInt(event.target.value, 10)
        getPrizes(1, perPage, state.status, state.field, state.order, filter.selectedCategories, filter.selectedPrizes, filter.selectedSubCategories)
    }

    const changeStatus = async (prize: Prize) => {
        if (checkPermission(permissions, "Lifestyle Prizes", "Update")) {
            prize.prizeCategory = prize.categoryid || ''
            prize.subCategory = prize.subCategoryid
            prize.active = !prize.active
            prize.isStatus = true
            const result = await dataProvider.update("prizes", {
                data: prize,
                id: prize._id,
                previousData: {} as any
            });
            if (result.data.result.active && result.data.result.isTrending) {
                await dataProvider.addFeatured("PRIZE",result.data.result._id)
            } else {
                await dataProvider.removeFeatured("PRIZE",result.data.result._id)
            }
            getPrizes(state.page, state.perPage, state.status, state.field, state.order, filter.selectedCategories, filter.selectedPrizes, filter.selectedSubCategories)
        }
    }

    const filterDropdown = (title: string) => {
        const tableHead = selectDropdown(title)
        if (tableHead) {
            setDropdown({ ...dropdown, [tableHead]: !dropdown[tableHead] })
        }
    }

    const handleSort = (direction: string, field: string) => {
        field = getFieldByTitle(field)
        getPrizes(state.page, state.perPage, state.status, field, direction.toLowerCase(), filter.selectedCategories, filter.selectedPrizes, filter.selectedSubCategories)
    }

    const closeFilter = () => {
        setDropdown({
            categoryFilter: false,
            subCategoryFilter: false,
            titleFilter: false
        })
    }

    const openModal = (id: string): void => {
        setModal({ ...modal, showModal: true, id: id })
    }

    const closeModal = (): void => {
        setModal({ ...modal, showModal: false, id: '' })
    }

    const removePrize = async (): Promise<void> => {
        try {
            const result = await dataProvider.delete('prizes', {
                id: modal.id,
                previousData: {} as Prize
            })
            if (result.data && result.data.message) {
                closeModal()
                await dataProvider.removeFeatured("PRIZE",modal.id)
                getPrizes(state.page, state.perPage, state.status, state.field, state.order, filter.selectedCategories, filter.selectedPrizes, filter.selectedSubCategories)
                notify(result.data.message, "info")
            }
        } catch (error) {
            notify("Prize not found", "error")
        }
    }

    return (
        <>
            <PrizeListTitle
                activeFilter={statusFilter}
                title="Lifestyle Prizes"
                path="prizes"
                isPrize={true}
                permission={checkPermission(permissions, "Lifestyle Prizes", "Update")}
            />
            <Card className="prize-table-card">
                <TableContainer>
                    <Table aria-label="simple table">
                        <TableHead>
                            <TableRow className="order-row">
                                <TableCell id="active-table-head" align="left">
                                    <HeadTable title="#" sort={handleSort} />
                                </TableCell>
                                <TableCell id="active-table-head" align="left">
                                    <HeadTable title="Title" sort={handleSort} filterHandle={filterDropdown} />
                                    {
                                        dropdown.titleFilter
                                        &&
                                        <FilterComponent
                                            handleSelectedCategory={selectedPrizes}
                                            search={searchPrize}
                                            categories={filter.prizes}
                                            clearCategory={clearSelectedFilter}
                                            selectedCategories={filter.selectedPrizes}
                                            selectedAllCategories={selectedAllPrizes}
                                            applyCategoryFilter={applyTitleFilter}
                                            appointment="selectedPrizes"
                                            searchString={searchState.selectedPrizes}
                                        />
                                    }
                                </TableCell>
                                <TableCell id="active-table-head" align="left">
                                    <HeadTable title="Price" sort={handleSort} />
                                </TableCell>
                                <TableCell id="active-table-head" align="left">
                                    <HeadTable title="Category" sort={handleSort} filterHandle={filterDropdown} />
                                    {
                                        dropdown.categoryFilter
                                        &&
                                        <FilterComponent
                                            handleSelectedCategory={selectedCategories}
                                            search={searchCategory}
                                            categories={filter.categories}
                                            clearCategory={clearSelectedFilter}
                                            selectedCategories={filter.selectedCategories}
                                            applyCategoryFilter={applyCategoryFilter}
                                            selectedAllCategories={selectAllCategories}
                                            appointment="selectedCategories"
                                            searchString={searchState.selectedCategories}
                                        />
                                    }
                                </TableCell>
                                <TableCell id="active-table-head" align="left">
                                    <HeadTable title="Sub-Category" sort={handleSort} filterHandle={filterDropdown} />
                                    {
                                        dropdown.subCategoryFilter
                                        &&
                                        <FilterComponent
                                            handleSelectedCategory={selectedSubCategories}
                                            search={searchSubCategory}
                                            categories={filter.subCategories}
                                            selectedCategories={filter.selectedSubCategories}
                                            clearCategory={clearSelectedFilter}
                                            applyCategoryFilter={applySubCategoriesFilter}
                                            selectedAllCategories={selectAllSubCategories}
                                            appointment="selectedSubCategories"
                                            searchString={searchState.selectedSubCategories}
                                        />
                                    }
                                </TableCell>
                                <TableCell id="discount-table-head" align="left">
                                    <HeadTable title="Discount" sort={handleSort} filterHandle={filterDropdown} />
                                </TableCell>
                                <TableCell id="status-table-head-actions" align="left">Status</TableCell>
                                {
                                    checkPermission(permissions, "Lifestyle Prizes", "Update") &&
                                    <TableCell id="active-table-head-actions" align="left">Actions</TableCell>
                                }
                            </TableRow>
                        </TableHead>
                        {state.prizes ? state.prizes.map((item: Prize) => {
                            return (
                                <TableBody key={item.id} onClick={closeFilter}>
                                    <TableRow className="order-row">
                                        <TableCell component="th">
                                            {item._id}
                                        </TableCell>
                                        <TableCell>
                                            {item.title}
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" justifyContent="center">
                                                {item.ticketPrice}
                                            </Box>
                                        </TableCell>
                                        <TableCell className="category-cell">
                                            {item.categorytitle || ''}
                                        </TableCell>
                                        <TableCell >
                                            {item.subcategorytitle || ''}
                                        </TableCell>
                                        <TableCell >
                                            <Box display="flex" justifyContent="center">
                                                {item.isActiveDiscount ? <DoneOutlineIcon /> : null}
                                            </Box>
                                        </TableCell>
                                        <TableCell id="status-table-head-actions">
                                            <label className="switch">
                                                <input type="checkbox" checked={item.active} onClick={() => changeStatus(item)} />
                                                <span className="slider round"></span>
                                            </label>
                                        </TableCell>
                                        {
                                            checkPermission(permissions, "Lifestyle Prizes", "Update") &&
                                            <TableCell>
                                                <div className="actions-table-body">
                                                    <CloneButton record={item} to={`prizes/${item.id}`} onClick={markClone} className="clone-prize" />
                                                    <EditButton to={`prizes/${item.id}`} className="edit-prize" />
                                                    {
                                                        checkPermission(permissions, "Lifestyle Prizes", "Delete") &&
                                                        <div onClick={() => openModal(item._id)}>
                                                            <DeleteForeverIcon className="delete-icon" />
                                                        </div>
                                                    }
                                                </div>
                                            </TableCell>
                                        }
                                    </TableRow>
                                </TableBody>
                            )
                        }) : null}
                    </Table>
                </TableContainer>
                <PaginationTable
                    page={state.page}
                    perPage={state.perPage}
                    collectionLength={state.prizes.length}
                    total={state.total}
                    handleChangePage={handleChangePage}
                    handleLastPage={handleLastPage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    handleFirstPage={handleFirstPage}
                    title="Prizes"
                />
                <Dialog
                    fullWidth
                    open={modal.showModal}
                    onClose={closeModal}
                >
                    <DialogContent>
                        <RemoveItem closeModal={closeModal} removeUser={removePrize} mainText="lifestyle prize" minorText="All information about the prize will be completely remove
                    from the system" />
                    </DialogContent>
                </Dialog>
            </Card>
        </>
    )
}

export default PrizesList